import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { searchChemists } from '@/services/taskService';
import { IGNORE_PW_MANAGERS } from '@/utils/formHints';

/**
 * Searchable pharmacy picker (tblChemists). `value` is { id, name } or null.
 * Debounced server search; dropdown is portalled to avoid modal clipping.
 */
const ChemistPicker = ({ value, onChange }) => {
  const [open, setOpen]       = useState(false);
  const [query, setQuery]     = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pos, setPos]         = useState(null);
  const anchorRef = useRef(null);
  const popRef    = useRef(null);

  const place = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const height = 260;
    const top = below < height + 8 && r.top > below ? r.top - height - 6 : r.bottom + 6;
    setPos({ top, left: r.left, width: r.width, maxHeight: Math.max(160, Math.min(height, Math.max(below, r.top) - 16)) });
  };

  // Debounced search whenever the dropdown is open and the query changes.
  useEffect(() => {
    if (!open) return;
    place();
    setLoading(true);
    const t = setTimeout(async () => {
      try { setResults(await searchChemists(query) || []); }
      catch { setResults([]); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [query, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => place();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (c) => { onChange({ id: c.id, name: c.name, phuuid: c.phuuid || null }); setOpen(false); setQuery(''); };
  const clear  = (e) => { e.stopPropagation(); onChange(null); };

  return (
    <>
      <button type="button" ref={anchorRef} onClick={() => { setOpen((o) => !o); setQuery(''); }}
        className={`mt-1 w-full flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-left transition ${open ? 'ring-2 ring-blue-300' : ''}`}>
        <StorefrontOutlinedIcon style={{ fontSize: 16 }} className="text-gray-400 flex-shrink-0" />
        <span className={`flex-1 truncate ${value ? 'text-gray-800' : 'text-gray-400'}`}>
          {value ? value.name : 'No pharmacy linked'}
        </span>
        {value && (
          <span role="button" onClick={clear} title="Remove" className="text-gray-400 hover:text-red-600 flex-shrink-0">
            <CloseIcon style={{ fontSize: 15 }} />
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <div ref={popRef} className="fixed z-[2000] bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col"
          style={{ top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}>
          <div className="p-2 border-b border-gray-100 relative flex-shrink-0">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" style={{ fontSize: 16 }} />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search pharmacy…"
              {...IGNORE_PW_MANAGERS}
              className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300" />
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {loading ? (
              <div className="flex justify-center py-4"><CircularProgress size={18} style={{ color: '#002169' }} /></div>
            ) : results.length === 0 ? (
              <p className="px-3 py-3 text-xs text-gray-400 italic">{query ? `No pharmacies match "${query}"` : 'Type to search pharmacies…'}</p>
            ) : results.map((c) => (
              <button key={c.id} type="button" onClick={() => choose(c)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition ${value?.id === c.id ? 'bg-blue-50 text-blue-800 font-semibold' : 'text-gray-700'}`}>
                {c.name}
                {c.phone && <span className="block text-[10px] text-gray-400">{c.phone}</span>}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ChemistPicker;
